import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Divider,
  IconButton,
  Paper,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useApiGet } from '../../utils/httpClientTs';
import {
  getMarketplaceCategories,
  getMarketplaceSubcategories,
  getMarketplaceItem,
  updateMarketplaceItem,
  uploadMarketplaceImage,
  uploadMarketplaceDocument,
  deleteMarketplaceImage,
  deleteMarketplaceDocument,
} from '../../endpoints/marketplace';
import { getPostalInfo } from '../../endpoints/lookupsTs';
import { MarketplaceImageUpload } from '../../Components/Marketplace';
import ChapterSelector from '../../Components/ChapterSelector';
import CountrySelector from '../../Components/CountrySelector';
import StateSelector from '../../Components/StateSelector';
import { emailValidator, phoneNumberValidator } from '../../utils/validators';
import { roles, applications } from '../../utils/variables';

interface MarketplaceFormData {
  name: string;
  description: string;
  price: string;
  category_id: string;
  subcategory_id: string;
  tags: string[];
  order_instructions: string;
  shipping_details: string;
  link_url: string;
  contact_name: string;
  show_contact_name: boolean;
  contact_email: string;
  show_contact_email: boolean;
  contact_phone: string;
  show_contact_phone: boolean;
  contact_address_1: string;
  contact_address_2: string;
  contact_city: string;
  contact_state_id: string;
  contact_state: string;
  contact_postal_code: string;
  contact_country_id: string;
  contact_country: string;
  show_contact_address: boolean;
  local_chapter_id: string;
  local_chapter: string;
  expiration_date: string;
  images: File[];
  documents: File[];
  existing_images: any[];
  existing_documents: any[];
}

interface FormErrors {
  [key: string]: string;
}

const MarketplaceEdit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [deletingImageId, setDeletingImageId] = useState<string | null>(null);
  const [deletingDocumentId, setDeletingDocumentId] = useState<string | null>(
    null
  );
  const [imageMetadata, setImageMetadata] = useState<
    Array<{
      file: File;
      isPrimary: boolean;
      displayOrder: number;
    }>
  >([]);
  const [documentMetadata, setDocumentMetadata] = useState<
    Array<{
      file: File;
      displayName: string;
    }>
  >([]);
  const [tagInput, setTagInput] = useState('');
  const documentInputRef = useRef<HTMLInputElement>(null);

  // Fetch existing item data
  const {
    data: existingItem,
    isLoading: isLoadingItem,
    error: itemError,
    refetch: refetchItem,
  } = useApiGet(['marketplace-item', id], () => getMarketplaceItem(id!), {
    enabled: !!id,
  });

  // Fetch categories and subcategories
  const { data: categories = [] } = useApiGet(
    ['marketplace-categories'],
    getMarketplaceCategories
  );
  const { data: subCategories = [] } = useApiGet(
    ['marketplace-subcategories', ''],
    getMarketplaceSubcategories
  );

  const [formData, setFormData] = useState<MarketplaceFormData>({
    name: '',
    description: '',
    price: '',
    category_id: '',
    subcategory_id: '',
    tags: [],
    order_instructions: '',
    shipping_details: '',
    link_url: '',
    contact_name: '',
    show_contact_name: true,
    contact_email: '',
    show_contact_email: true,
    contact_phone: '',
    show_contact_phone: true,
    contact_address_1: '',
    contact_address_2: '',
    contact_city: '',
    contact_state_id: '',
    contact_state: '',
    contact_postal_code: '',
    contact_country_id: '',
    contact_country: '',
    show_contact_address: false,
    local_chapter_id: '',
    local_chapter: '',
    expiration_date: '',
    images: [],
    documents: [],
    existing_images: [],
    existing_documents: [],
  });

  // Populate form data when item is loaded
  useEffect(() => {
    if (existingItem) {
      setFormData({
        name: existingItem.name || '',
        description: existingItem.description || '',
        price: existingItem.price || '',
        category_id: existingItem.category_id || '',
        subcategory_id: existingItem.subcategory_id || '',
        tags: existingItem.tags || [],
        order_instructions: existingItem.order_instructions || '',
        shipping_details: existingItem.shipping_details || '',
        link_url: existingItem.link_url || '',
        contact_name: existingItem.contact_name || '',
        show_contact_name: existingItem.show_contact_name ?? true,
        contact_email: existingItem.contact_email || '',
        show_contact_email: existingItem.show_contact_email ?? true,
        contact_phone: existingItem.contact_phone || '',
        show_contact_phone: existingItem.show_contact_phone ?? true,
        contact_address_1: existingItem.contact_address_1 || '',
        contact_address_2: existingItem.contact_address_2 || '',
        contact_city: existingItem.contact_city || '',
        contact_state_id: existingItem.contact_state_id || '',
        contact_state: existingItem.contact_state || '',
        contact_postal_code: existingItem.contact_postal_code || '',
        contact_country_id: existingItem.contact_country_id || '',
        contact_country: existingItem.contact_country || '',
        show_contact_address: existingItem.show_contact_address ?? false,
        local_chapter_id: existingItem.local_chapter_id || '',
        local_chapter: existingItem.local_chapter || '',
        expiration_date: existingItem.expiration_date || '',
        images: [],
        documents: [],
        existing_images: existingItem.images || [],
        existing_documents: existingItem.document
          ? [existingItem.document]
          : [],
      });
    }
  }, [existingItem]);

  const canEdit = () => {
    const userRole = localStorage.getItem('role');
    const userApps = localStorage.getItem('apps');

    if (userRole === roles.Admin) {
      return true;
    }

    if (userApps && userApps.includes(applications.MarketplaceAdmin)) {
      return true;
    }

    return true; // Default to true for now
  };

  const handleInputChange = useCallback(
    (field: keyof MarketplaceFormData, value: any) => {
      setFormData((prev) => {
        const newData = {
          ...prev,
          [field]: value,
        };

        // Clear subcategory when category changes
        if (field === 'category_id') {
          newData.subcategory_id = '';
        }

        return newData;
      });

      // Clear validation errors when user types
      if (formErrors[field]) {
        setFormErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    },
    [formErrors]
  );

  // Check if the selected category has subcategories
  const hasSubcategories =
    subCategories &&
    subCategories.filter((sub) => sub.category_id === formData.category_id)
      .length > 0;

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const handleRemoveDocument = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index),
    }));
    setDocumentMetadata((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDocumentMetadataChange = (
    index: number,
    field: 'displayName',
    value: string
  ) => {
    setDocumentMetadata((prev) => {
      const newMetadata = [...prev];
      newMetadata[index] = {
        ...newMetadata[index],
        [field]: value,
      };
      return newMetadata;
    });
  };

  const handleRemoveExistingImage = async (imageId: string) => {
    try {
      // Show loading state
      setError(null);
      setDeletingImageId(imageId);

      // Call the API to delete the image
      await deleteMarketplaceImage(id!, imageId);

      // If we get here, the API call succeeded - remove from UI
      setFormData((prev) => ({
        ...prev,
        existing_images: prev.existing_images.filter(
          (img) => img.id !== imageId
        ),
      }));
    } catch (error) {
      // Only show error if deletion failed - item stays in UI
      setError(
        `Failed to remove image: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    } finally {
      setDeletingImageId(null);
    }
  };

  const handleRemoveExistingDocument = async (documentId: string) => {
    try {
      // Show loading state
      setError(null);
      setDeletingDocumentId(documentId);

      // Call the API to delete the document
      await deleteMarketplaceDocument(id!, documentId);

      // If we get here, the API call succeeded - remove from UI
      setFormData((prev) => ({
        ...prev,
        existing_documents: prev.existing_documents.filter(
          (doc) => doc.id !== documentId
        ),
      }));
    } catch (error) {
      // Only show error if deletion failed - item stays in UI
      console.error('Error removing document:', error);
      setError(
        `Failed to remove document: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    } finally {
      setDeletingDocumentId(null);
    }
  };

  // Country change handler
  const handleCountryChange = (country: any) => {
    handleInputChange('contact_country_id', country?.id || '');
    handleInputChange('contact_country', country?.name || '');
    // Reset state and city when country changes
    handleInputChange('contact_state_id', '');
    handleInputChange('contact_state', '');
    handleInputChange('contact_city', '');
  };

  // State change handler
  const handleStateChange = (state: any) => {
    handleInputChange('contact_state_id', state?.id || '');
    handleInputChange('contact_state', state?.name || '');
  };

  // Postal code lookup effect
  useEffect(() => {
    const lookupPostalCode = async () => {
      if (formData.contact_postal_code && formData.contact_country_id) {
        try {
          const data = await getPostalInfo(
            formData.contact_postal_code,
            formData.contact_country_id
          );
          if (data.city) {
            handleInputChange('contact_city', data.city);
          }
          if (data.state_id) {
            handleInputChange('contact_state_id', data.state_id);
          }
        } catch (error) {
          console.error('Error looking up postal code:', error);
        }
      }
    };

    lookupPostalCode();
  }, [
    formData.contact_postal_code,
    formData.contact_country_id,
    handleInputChange,
  ]);

  // Image change handler
  const handleImagesChange = (newImages: File[]) => {
    setFormData((prev) => ({
      ...prev,
      images: newImages,
    }));

    // Sync metadata with the new images array
    // Preserve metadata for files that still exist, create new metadata for new files
    const hasExistingPrimary = formData.existing_images.some(
      (img) => img.is_primary
    );

    const syncedMetadata = newImages.map((file, index) => {
      // Try to find existing metadata for this file
      const existingMeta = imageMetadata.find(
        (meta) => meta.file === file || meta.file.name === file.name
      );

      if (existingMeta) {
        // Preserve existing metadata
        return existingMeta;
      } else {
        // Create new metadata for new files
        return {
          file,
          isPrimary: !hasExistingPrimary && index === 0, // First image is primary only if no primary exists
          displayOrder: formData.existing_images.length + index, // Start from existing images count
        };
      }
    });

    setImageMetadata(syncedMetadata);
  };

  // Image metadata change handler
  const handleImageMetadataChange = (
    index: number,
    field: 'isPrimary' | 'displayOrder',
    value: any
  ) => {
    setImageMetadata((prev) => {
      const newMetadata = [...prev];
      newMetadata[index] = {
        ...newMetadata[index],
        [field]: value,
      };
      return newMetadata;
    });
  };

  // Document upload handler
  const handleDocumentUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setFormData((prev) => ({
      ...prev,
      documents: [...prev.documents, ...files],
    }));

    // Initialize metadata for new documents
    const newMetadata = files.map((file) => ({
      file,
      displayName: file.name, // Default to original filename
    }));
    setDocumentMetadata((prev) => [...prev, ...newMetadata]);

    // Reset input
    event.target.value = '';
  };

  // Validation function
  const validateStep = (step: number): boolean => {
    const errors: FormErrors = {};

    switch (step) {
      case 0: // Item Details
        if (!formData.name.trim()) errors.name = 'Item name is required';
        if (!formData.description.trim())
          errors.description = 'Description is required';
        if (!formData.price.trim()) errors.price = 'Price is required';
        if (!formData.category_id) errors.category_id = 'Category is required';
        break;
      case 1: // Images & Documents
        // No validation required for this step
        break;
      case 2: // Contact Information
        if (!formData.contact_name)
          errors.contact_name = 'Contact name is required';
        if (!formData.contact_email) {
          errors.contact_email = 'Email is required';
        } else if (emailValidator(formData.contact_email)) {
          errors.contact_email = 'Please enter a valid email address';
        }
        if (!formData.contact_phone) {
          errors.contact_phone = 'Phone number is required';
        } else if (phoneNumberValidator(formData.contact_phone)) {
          errors.contact_phone = 'Please enter a valid phone number';
        }
        if (!formData.expiration_date)
          errors.expiration_date = 'Expiration date is required';
        // Address fields are now optional, but if provided, validate them
        if (formData.contact_country_id && !formData.contact_postal_code)
          errors.contact_postal_code =
            'Postal code is required when country is selected';
        if (
          formData.contact_country_id === '1' &&
          formData.show_contact_address &&
          !formData.contact_state_id
        )
          errors.contact_state_id = 'State is required for US addresses';
        break;
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const isStepValid = (step: number): boolean => {
    switch (step) {
      case 0:
        return !!(
          formData.name.trim() &&
          formData.description.trim() &&
          formData.price.trim() &&
          formData.category_id
        );
      case 1:
        return true; // Images and documents are optional
      case 2:
        return (
          formData.contact_name &&
          formData.contact_email &&
          formData.contact_phone &&
          formData.expiration_date &&
          !emailValidator(formData.contact_email) &&
          !phoneNumberValidator(formData.contact_phone)
        );
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    if (!canEdit()) {
      setError('You do not have permission to edit this item');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Validate that if we have images to upload, we have metadata for them
      if (
        formData.images.length > 0 &&
        formData.images.length !== imageMetadata.length
      ) {
        throw new Error(
          `Image metadata mismatch. Found ${formData.images.length} image(s) but ${imageMetadata.length} metadata entry/entries. Please refresh and try again.`
        );
      }

      // Validate that if we have documents to upload, we have metadata for them
      if (
        formData.documents.length > 0 &&
        formData.documents.length !== documentMetadata.length
      ) {
        throw new Error(
          'Document metadata mismatch. Please refresh and try again.'
        );
      }

      // Upload new images first with validation
      const uploadedImageIds: string[] = [];
      const uploadErrors: string[] = [];

      for (let i = 0; i < formData.images.length; i++) {
        const file = formData.images[i];
        try {
          const response = await uploadMarketplaceImage(file);

          // Validate response has the required ID
          if (!response?.data?.id) {
            uploadErrors.push(
              `Failed to upload image "${file.name}": No image ID returned`
            );
            continue;
          }

          uploadedImageIds.push(response.data.id);
        } catch (error: any) {
          const errorMessage =
            error?.message || error?.response?.data?.message || 'Unknown error';
          uploadErrors.push(
            `Failed to upload image "${file.name}": ${errorMessage}`
          );
        }
      }

      // If any image uploads failed, throw error
      if (uploadErrors.length > 0) {
        throw new Error(`Image upload failed:\n${uploadErrors.join('\n')}`);
      }

      // Validate that all images we tried to upload have IDs
      if (
        formData.images.length > 0 &&
        uploadedImageIds.length !== formData.images.length
      ) {
        throw new Error(
          `Image upload incomplete. Expected ${formData.images.length} image(s), but only ${uploadedImageIds.length} uploaded successfully.`
        );
      }

      // Upload new documents with validation
      const uploadedDocuments: Array<{ id: string; name: string }> = [];
      const documentUploadErrors: string[] = [];

      for (let i = 0; i < formData.documents.length; i++) {
        const file = formData.documents[i];
        const metadata = documentMetadata[i];
        try {
          const response = await uploadMarketplaceDocument(file);

          // Validate response has the required ID
          if (!response?.data?.id) {
            documentUploadErrors.push(
              `Failed to upload document "${file.name}": No document ID returned`
            );
            continue;
          }

          // Validate metadata exists
          if (!metadata) {
            documentUploadErrors.push(
              `Missing metadata for document "${file.name}"`
            );
            continue;
          }

          uploadedDocuments.push({
            id: response.data.id,
            name: metadata.displayName || file.name,
          });
        } catch (error: any) {
          const errorMessage =
            error?.message || error?.response?.data?.message || 'Unknown error';
          documentUploadErrors.push(
            `Failed to upload document "${file.name}": ${errorMessage}`
          );
        }
      }

      // If any document uploads failed, throw error
      if (documentUploadErrors.length > 0) {
        throw new Error(
          `Document upload failed:\n${documentUploadErrors.join('\n')}`
        );
      }

      // Validate that all documents we tried to upload have IDs
      if (
        formData.documents.length > 0 &&
        uploadedDocuments.length !== formData.documents.length
      ) {
        throw new Error(
          `Document upload incomplete. Expected ${formData.documents.length} document(s), but only ${uploadedDocuments.length} uploaded successfully.`
        );
      }

      // Helper function to convert empty strings to null
      const emptyStringToNull = (
        value: string | null | undefined
      ): string | null => {
        return value === '' ? null : value || null;
      };

      // Prepare update data
      const updateData = {
        name: formData.name,
        description: formData.description,
        price: formData.price,
        category_id: formData.category_id,
        subcategory_id: emptyStringToNull(formData.subcategory_id),
        tags: formData.tags,
        order_instructions: emptyStringToNull(formData.order_instructions),
        shipping_details: emptyStringToNull(formData.shipping_details),
        link_url: emptyStringToNull(formData.link_url),
        contact_name: formData.contact_name,
        show_contact_name: formData.show_contact_name,
        contact_email: formData.contact_email,
        show_contact_email: formData.show_contact_email,
        contact_phone: formData.contact_phone,
        show_contact_phone: formData.show_contact_phone,
        contact_address_1: emptyStringToNull(formData.contact_address_1),
        contact_address_2: emptyStringToNull(formData.contact_address_2),
        contact_city: emptyStringToNull(formData.contact_city),
        contact_state_id: emptyStringToNull(formData.contact_state_id),
        contact_postal_code: emptyStringToNull(formData.contact_postal_code),
        contact_country_id: emptyStringToNull(formData.contact_country_id),
        show_contact_address: formData.show_contact_address,
        local_chapter_id: emptyStringToNull(formData.local_chapter_id),
        expiration_date: formData.expiration_date,
        images: [
          // Include existing images with their current metadata
          ...formData.existing_images.map((img) => ({
            id: img.id,
            is_primary: img.is_primary || false,
            display_order: img.display_order ?? 0,
          })),
          // Include newly uploaded images with metadata from imageMetadata
          ...uploadedImageIds.map((id, index) => {
            const metadata = imageMetadata[index];
            if (!metadata) {
              throw new Error(
                `Missing metadata for uploaded image at index ${index}`
              );
            }
            return {
              id,
              is_primary: metadata.isPrimary || false,
              display_order:
                metadata.displayOrder ??
                formData.existing_images.length + index,
            };
          }),
        ],
        document:
          formData.existing_documents.length > 0
            ? {
                id: formData.existing_documents[0].id,
                name: formData.existing_documents[0].name,
              }
            : uploadedDocuments.length > 0
              ? {
                  id: uploadedDocuments[0].id,
                  name: uploadedDocuments[0].name,
                }
              : null,
      };

      // Final validation: Ensure all uploaded image IDs are in the PUT request
      const imageIdsInRequest = updateData.images.map((img) => img.id);
      const missingImageIds = uploadedImageIds.filter(
        (id) => !imageIdsInRequest.includes(id)
      );

      if (missingImageIds.length > 0) {
        throw new Error(
          `Validation error: Uploaded image IDs [${missingImageIds.join(', ')}] are missing from the update request.`
        );
      }

      // Validate that we have the expected number of images
      const expectedImageCount =
        formData.existing_images.length + uploadedImageIds.length;
      if (updateData.images.length !== expectedImageCount) {
        throw new Error(
          `Image count mismatch. Expected ${expectedImageCount} image(s) in request, but found ${updateData.images.length}.`
        );
      }

      // Final validation: Ensure uploaded document ID is in the PUT request if we uploaded documents
      if (uploadedDocuments.length > 0) {
        const uploadedDocumentId = uploadedDocuments[0].id;
        if (
          !updateData.document ||
          updateData.document.id !== uploadedDocumentId
        ) {
          throw new Error(
            `Validation error: Uploaded document ID "${uploadedDocumentId}" is missing from the update request.`
          );
        }
      }

      // Update the item
      await updateMarketplaceItem(id!, updateData);

      // Refetch the item data to get the latest information
      await refetchItem();

      // Invalidate the query cache so the detail page also gets fresh data
      queryClient.invalidateQueries({ queryKey: ['marketplace-item', id] });

      // Navigate to detail page
      navigate(`/marketplace/${id}`);
    } catch (error: any) {
      console.error('Error updating item:', error);
      const errorMessage =
        error?.message ||
        error?.response?.data?.message ||
        'Failed to update item. Please try again.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const steps = [
    'Item Details',
    'Images & Documents',
    'Contact Information',
    'Review & Submit',
  ];

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0: // Item Details
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Item Name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                error={!!formErrors.name}
                helperText={formErrors.name}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={4}
                value={formData.description}
                onChange={(e) =>
                  handleInputChange('description', e.target.value)
                }
                error={!!formErrors.description}
                helperText={formErrors.description}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Price"
                value={formData.price}
                onChange={(e) => handleInputChange('price', e.target.value)}
                placeholder="e.g., $50 or Prices vary"
                error={!!formErrors.price}
                helperText={formErrors.price}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Tags"
                placeholder="Add a tag and press Enter"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        onClick={handleAddTag}
                        variant="outlined"
                        size="small"
                      >
                        Add
                      </Button>
                    </InputAdornment>
                  ),
                }}
                helperText={
                  formData.tags.length > 0 ? (
                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      sx={{ mt: 1 }}
                    >
                      {formData.tags.map((tag, index) => (
                        <Chip
                          key={index}
                          label={tag}
                          onDelete={() => handleRemoveTag(tag)}
                          size="small"
                        />
                      ))}
                    </Stack>
                  ) : (
                    'Press Enter or click Add to add tags'
                  )
                }
              />
            </Grid>
            <Grid item xs={12} md={hasSubcategories ? 6 : 12}>
              <FormControl fullWidth required>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category_id}
                  onChange={(e) =>
                    handleInputChange('category_id', e.target.value)
                  }
                  label="Category"
                  error={!!formErrors.category_id}
                >
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {hasSubcategories && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Subcategory</InputLabel>
                  <Select
                    value={formData.subcategory_id}
                    onChange={(e) =>
                      handleInputChange('subcategory_id', e.target.value)
                    }
                    label="Subcategory"
                  >
                    {subCategories
                      .filter((sub) => sub.category_id === formData.category_id)
                      .map((subcategory) => (
                        <MenuItem key={subcategory.id} value={subcategory.id}>
                          {subcategory.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Order Instructions"
                multiline
                rows={3}
                value={formData.order_instructions}
                onChange={(e) =>
                  handleInputChange('order_instructions', e.target.value)
                }
                placeholder="How should customers order this item?"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Shipping Information"
                multiline
                rows={3}
                value={formData.shipping_details}
                onChange={(e) =>
                  handleInputChange('shipping_details', e.target.value)
                }
                placeholder="Shipping details, costs, etc."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Link URL (Optional)"
                value={formData.link_url}
                onChange={(e) => handleInputChange('link_url', e.target.value)}
                placeholder="https://example.com"
              />
            </Grid>
          </Grid>
        );

      case 1: // Images & Documents
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <MarketplaceImageUpload
                images={formData.images}
                onImagesChange={handleImagesChange}
                imageMetadata={imageMetadata}
                onImageMetadataChange={handleImageMetadataChange}
                maxImages={10}
                maxSize={5}
              />
            </Grid>
            {formData.existing_images.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Existing Images
                </Typography>
                <Grid container spacing={2}>
                  {formData.existing_images.map((image, index) => (
                    <Grid item xs={12} sm={6} md={4} key={image.id}>
                      <Card>
                        <CardContent sx={{ p: 1 }}>
                          <Box
                            sx={{
                              position: 'relative',
                              height: '150px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: '#f5f5f5',
                              borderRadius: 1,
                              overflow: 'hidden',
                            }}
                          >
                            <img
                              src={image.url}
                              alt={image.alt_text || 'Marketplace item'}
                              style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                                width: 'auto',
                                height: 'auto',
                              }}
                            />
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleRemoveExistingImage(image.id)
                              }
                              disabled={deletingImageId === image.id}
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                },
                              }}
                              color="error"
                            >
                              {deletingImageId === image.id ? (
                                <CircularProgress size={16} />
                              ) : (
                                <DeleteIcon />
                              )}
                            </IconButton>
                          </Box>
                          <Typography
                            variant="caption"
                            display="block"
                            sx={{ mt: 1, textAlign: 'center' }}
                          >
                            {image.is_primary
                              ? 'Primary Image'
                              : 'Secondary Image'}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            )}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Documents (Optional)
              </Typography>
              <Box sx={{ textAlign: 'center' }}>
                <input
                  ref={documentInputRef}
                  id="document-upload"
                  type="file"
                  multiple
                  onChange={handleDocumentUpload}
                  style={{ display: 'none' }}
                />
                <label htmlFor="document-upload">
                  <Paper
                    sx={{
                      p: 2,
                      border: '2px dashed #ccc',
                      textAlign: 'center',
                      cursor: 'pointer',
                      '&:hover': {
                        borderColor: '#FF6B35',
                        backgroundColor: 'rgba(255, 107, 53, 0.04)',
                      },
                    }}
                  >
                    <CloudUploadIcon
                      sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Click to upload documents (order forms, etc.)
                    </Typography>
                  </Paper>
                </label>
              </Box>
              {formData.documents.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  {formData.documents.map((document, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        p: 2,
                        border: '1px solid #ddd',
                        borderRadius: 1,
                        mb: 2,
                        backgroundColor: '#f9f9f9',
                      }}
                    >
                      <Box sx={{ flexGrow: 1, mr: 2 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Document Name"
                          value={
                            documentMetadata[index]?.displayName ||
                            document.name
                          }
                          onChange={(e) =>
                            handleDocumentMetadataChange(
                              index,
                              'displayName',
                              e.target.value
                            )
                          }
                          helperText={`Original: ${document.name}`}
                        />
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveDocument(index)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
              {formData.existing_documents.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Existing Documents
                  </Typography>
                  {formData.existing_documents.map((document, index) => (
                    <Box
                      key={document.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        p: 1,
                        border: '1px solid #ddd',
                        borderRadius: 1,
                        mb: 1,
                        backgroundColor: '#f9f9f9',
                      }}
                    >
                      <Typography variant="body2" sx={{ flexGrow: 1 }}>
                        {document.name}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleRemoveExistingDocument(document.id)
                        }
                        disabled={deletingDocumentId === document.id}
                        color="error"
                      >
                        {deletingDocumentId === document.id ? (
                          <CircularProgress size={16} />
                        ) : (
                          <DeleteIcon />
                        )}
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Grid>
          </Grid>
        );

      case 2: // Contact Information
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Contact Name"
                value={formData.contact_name}
                onChange={(e) =>
                  handleInputChange('contact_name', e.target.value)
                }
                error={!!formErrors.contact_name}
                helperText={formErrors.contact_name}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.contact_email}
                onChange={(e) =>
                  handleInputChange('contact_email', e.target.value)
                }
                error={!!formErrors.contact_email}
                helperText={formErrors.contact_email}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.contact_phone}
                onChange={(e) =>
                  handleInputChange('contact_phone', e.target.value)
                }
                error={!!formErrors.contact_phone}
                helperText={formErrors.contact_phone}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Expiration Date"
                type="date"
                value={formData.expiration_date}
                onChange={(e) =>
                  handleInputChange('expiration_date', e.target.value)
                }
                InputLabelProps={{
                  shrink: true,
                }}
                error={!!formErrors.expiration_date}
                helperText={formErrors.expiration_date}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Contact Information Visibility
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Choose which contact information to display to shoppers
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl component="fieldset">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <input
                    type="checkbox"
                    checked={formData.show_contact_name}
                    onChange={(e) =>
                      handleInputChange('show_contact_name', e.target.checked)
                    }
                  />
                  <Typography variant="body2">Show Contact Name</Typography>
                </Stack>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl component="fieldset">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <input
                    type="checkbox"
                    checked={formData.show_contact_email}
                    onChange={(e) =>
                      handleInputChange('show_contact_email', e.target.checked)
                    }
                  />
                  <Typography variant="body2">Show Email</Typography>
                </Stack>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl component="fieldset">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <input
                    type="checkbox"
                    checked={formData.show_contact_phone}
                    onChange={(e) =>
                      handleInputChange('show_contact_phone', e.target.checked)
                    }
                  />
                  <Typography variant="body2">Show Phone</Typography>
                </Stack>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <ChapterSelector
                chapter={formData.local_chapter_id}
                handleChapterSelect={(value) =>
                  handleInputChange('local_chapter_id', value)
                }
                spd={null}
                reportId={null}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Address Information (Optional)
                </Typography>
              </Divider>
            </Grid>
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <input
                    type="checkbox"
                    checked={formData.show_contact_address}
                    onChange={(e) =>
                      handleInputChange(
                        'show_contact_address',
                        e.target.checked
                      )
                    }
                  />
                  <Typography variant="body2">
                    Show Address Information
                  </Typography>
                </Stack>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <CountrySelector
                value={formData.contact_country_id}
                handleChange={handleCountryChange}
                error={!!formErrors.contact_country_id}
                errorMessage={formErrors.contact_country_id}
                required={false}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Postal Code"
                value={formData.contact_postal_code}
                onChange={(e) =>
                  handleInputChange('contact_postal_code', e.target.value)
                }
                error={!!formErrors.contact_postal_code}
                helperText={formErrors.contact_postal_code}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="City"
                value={formData.contact_city}
                onChange={(e) =>
                  handleInputChange('contact_city', e.target.value)
                }
                error={!!formErrors.contact_city}
                helperText={formErrors.contact_city}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <StateSelector
                countryId={formData.contact_country_id}
                value={formData.contact_state_id}
                handleChange={handleStateChange}
                error={!!formErrors.contact_state_id}
                errorMessage={formErrors.contact_state_id}
                required={false}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 1"
                value={formData.contact_address_1}
                onChange={(e) =>
                  handleInputChange('contact_address_1', e.target.value)
                }
                error={!!formErrors.contact_address_1}
                helperText={formErrors.contact_address_1}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 2"
                value={formData.contact_address_2}
                onChange={(e) =>
                  handleInputChange('contact_address_2', e.target.value)
                }
                error={!!formErrors.contact_address_2}
                helperText={formErrors.contact_address_2}
              />
            </Grid>
            {Object.keys(formErrors).length > 0 && (
              <Grid item xs={12}>
                <Alert severity="error">
                  Please fix the following errors:
                  <ul>
                    {Object.entries(formErrors).map(([field, error]) => (
                      <li key={field}>{error}</li>
                    ))}
                  </ul>
                </Alert>
              </Grid>
            )}
          </Grid>
        );

      case 3: // Review & Submit
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Review Your Item
              </Typography>
              <Card variant="outlined">
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Item Name
                      </Typography>
                      <Typography variant="body1">{formData.name}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Price
                      </Typography>
                      <Typography variant="body1">{formData.price}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Description
                      </Typography>
                      <Typography variant="body1">
                        {formData.description}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Category
                      </Typography>
                      <Typography variant="body1">
                        {
                          categories.find((c) => c.id === formData.category_id)
                            ?.name
                        }
                      </Typography>
                    </Grid>
                    {formData.subcategory_id && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Subcategory
                        </Typography>
                        <Typography variant="body1">
                          {
                            subCategories.find(
                              (s) => s.id === formData.subcategory_id
                            )?.name
                          }
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Tags
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          {formData.tags.map((tag, index) => (
                            <Chip key={index} label={tag} size="small" />
                          ))}
                        </Stack>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Contact Name
                      </Typography>
                      <Typography variant="body1">
                        {formData.contact_name}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Email
                      </Typography>
                      <Typography variant="body1">
                        {formData.contact_email}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Phone
                      </Typography>
                      <Typography variant="body1">
                        {formData.contact_phone}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Chapter
                      </Typography>
                      <Typography variant="body1">
                        {formData.local_chapter || formData.local_chapter_id}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Address
                      </Typography>
                      <Typography variant="body1">
                        {formData.contact_address_1}
                        {formData.contact_address_2 &&
                          `, ${formData.contact_address_2}`}
                        <br />
                        {formData.contact_city}, {formData.contact_state}{' '}
                        {formData.contact_postal_code}
                        <br />
                        {formData.contact_country}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  if (isLoadingItem) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (itemError || !existingItem) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Error loading marketplace item</Alert>
      </Box>
    );
  }

  if (!canEdit()) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          You do not have permission to edit this item
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/marketplace/${id}`)}
          sx={{ mr: 2 }}
        >
          Back to Item
        </Button>
        <Typography variant="h4" component="h1">
          Edit Marketplace Item
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stepper */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Form Content */}
      <Card>
        <CardContent>{renderStepContent(activeStep)}</CardContent>
      </Card>

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button disabled={activeStep === 0} onClick={handleBack}>
          Back
        </Button>
        <Box>
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitting}
              startIcon={
                isSubmitting ? <CircularProgress size={20} /> : <MoneyIcon />
              }
            >
              {isSubmitting ? 'Updating...' : 'Update Item'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!isStepValid(activeStep)}
            >
              Next
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default MarketplaceEdit;
